import {catchError, defer, exhaustMap, map, of, startWith, Subject, switchMap, tap} from 'rxjs';
import {encodeFunctionData, parseUnits} from 'viem';
import {fetchBalance, fetchToken, getNetwork, prepareSendTransaction, sendTransaction, getWalletClient, switchNetwork, erc20ABI} from '@wagmi/core';
import {CustomObservableError, defineObservableSteps, execObservableSteps} from '@/streams/utils';
import {
  trackCommit,
  trackUserChangeChain,
  trackUserAddChain,
  trackChangeChainError,
  trackaddChainError,
  trackgetDecimalsError,
  trackTransactionSuccess,
  trackTransactionError,
  trackPreTransactionError,
  trackTransactionUnknowError,
} from '../../track';
import {getChain, type SendTransactionParams} from '../utils';
import {mc} from '@/core';

/** 表单提交 */
export const submit$ = new Subject<SubmitParams>();

const submitSteps = defineObservableSteps([
  {
    name: 'switchNetwork',
    generateObservable(params: SubmitParams, context) {
      trackCommit(params);

      const {network} = params;
      const {chain} = getNetwork();
      const safeChainId = Number(network);
      if (safeChainId !== Number(chain?.id)) {
        trackUserChangeChain({beforeChainId: safeChainId, beforeChainName: getChain(safeChainId)?.name, afterChainId: chain?.id, afterChainName: chain?.name});

        return defer(() => switchNetwork({chainId: safeChainId})).pipe(
          catchError((error) => {
            if (error?.name === 'ChainNotConfiguredForConnectorError') {
              const chain = getChain(safeChainId);
              trackUserAddChain({chainId: chain?.id, chainName: chain?.name});

              return defer(getWalletClient).pipe(
                switchMap((walletClient) => walletClient.addChain({chain})),
                catchError((addChainError) => {
                  trackaddChainError(addChainError);
                  throw addChainError;
                })
              );
            }
            throw error;
          }),
          catchError((error) => {
            if (error?.name === 'UserRejectedRequestError') {
              throw new CustomObservableError('user cancel request');
            }
            trackChangeChainError(error);
            throw error;
          })
        );
      }
      return of(params);
    },
  },
  {
    name: 'formatedAmount',
    generateObservable(params: SubmitParams, context) {
      const {walletAddress, contractAddress, network, amount} = params;
      const amountValue = `${amount}`;
      const queryParams = {address: walletAddress, chainId: network};
      const fetchBalanceParams = {...queryParams, ...(!contractAddress ? {} : {token: contractAddress})};

      return defer(() => fetchBalance(fetchBalanceParams as any)).pipe(
        map((result) => ({formatedAmount: parseUnits(amountValue, result.decimals || 6)})),
        catchError((error) => {
          return defer(() =>
            fetchToken({
              address: contractAddress as any,
              chainId: network as any,
            })
          ).pipe(
            map((result) => ({formatedAmount: parseUnits(amountValue, result.decimals || 6)})),
            catchError((error) => {
              trackgetDecimalsError(error);
              return of({formatedAmount: parseUnits(amountValue, 6)});
            })
          );
        })
      );
    },
  },
  {
    name: 'senPreTransaction',
    generateObservable(params: SubmitParams, context: {formatedAmount: bigint}) {
      const {walletAddress, contractAddress, network, receiveAddress} = params;
      const {formatedAmount} = context;
      const isNativeToken = !contractAddress;

      const commonParams = {
        account: walletAddress as any,
        chain: getChain(network),
      };
      const dynamicParams = isNativeToken
        ? {to: receiveAddress, value: formatedAmount}
        : {
            to: contractAddress,
            data: encodeFunctionData({
              abi: erc20ABI,
              functionName: 'transfer',
              args: [receiveAddress as any, formatedAmount],
            }),
          };
      const transactionParams = {
        // ...commonParams,
        ...dynamicParams,
      };
      return defer(() => prepareSendTransaction(transactionParams)).pipe(
        catchError((error) => {
          trackPreTransactionError(error);
          mc.send('toast', 'error', {message: error.details || error.message || 'prepare send transaction error'});
          throw new CustomObservableError(error);
        }),
        map((request) => ({
          request,
          transactionParams,
        }))
      );
    },
  },
  {
    name: 'sendTransaction',
    generateObservable(params: SubmitParams, context: {request; transactionParams}) {
      return of(context.request).pipe(
        switchMap(sendTransaction),
        tap((result) => {
          try {
            trackTransactionSuccess({
              ...(context.transactionParams || {}),
              ...(result as any),
            });
          } catch (error) {}
        }),
        catchError((error) => {
          const errorMessage = error?.message || '';
          trackTransactionError(error);
          if (errorMessage.includes('User rejected the request') || errorMessage.includes('An unknown RPC error occurred')) {
            throw new CustomObservableError('user cancel request');
          }
          throw error;
        })
      );
    },
  },
]);
export const submitState$ = submit$.pipe(
  exhaustMap((params) =>
    execObservableSteps(submitSteps, params).pipe(
      map(() => ({loading: false, data: {success: true}})),
      startWith({loading: true}),
      catchError((error) => {
        trackTransactionUnknowError(error);
        return of({loading: false});
      })
    )
  )
);

type SubmitParams = Omit<SendTransactionParams, 'amount'> & {
  amount: string | number;
};
